import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffect, useMemo, useState } from "react"
import { useAsyncCallback } from "@nova/use-async-callback"
import { GenericUpload, ImageProcessingState, JobStatusType, SidePanelLayer } from "@nova/constants"
import EventEmitter from "events"
import { get } from "nodash"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { selectUploadTimestampForFlightId } from "features/map/layers/selectors"
import { InspectorPanelMetadata } from "features/map/inspector-panel/FlightPanelMetadata"
import {
    ProgressBar,
    UploadProgressBar,
} from "panels/content/side-panel/upload-flow/components/UploadProgressBar"
import { ButtonDynamic, ButtonPrimary } from "@nova/component-btn"
import { GenericDataUsage } from "panels/content/side-panel/upload-flow/components/GenericDataUsage"
import { FlightUploadDropbox } from "panels/content/side-panel/upload-flow/components/FlightUploadDropbox"
import { DeleteFlight } from "panels/content/side-panel/upload-flow/components/DeleteFlight"
import { trimMiddle } from "@nova/util-strings"
import { deleteUpload } from "../ColorFinderSlice"
import { InspectorPanelKeyValueToggle } from "features/map/inspector-panel/InspectorPanelKeyValueToggle"
import { addUploadTimestamp } from "features/map/layers/slice"
import {
    flightExceededUsage,
    getSummaryFromUploads,
} from "panels/content/side-panel/upload-flow/module"
import { Layer } from "@nova/protobuf"
import { VideoColorfinderPreviewFilesDemo } from "./VideoColorfinderPreviewFilesDemo"
import { openColorFinder } from "plugins/ColorFinder/V2/redux/slice"

const browserSupported = typeof window === "undefined" ? true : "VideoFrame" in window

class ProgressEmitter extends EventEmitter {
    public progresses = {} as Record<string, number[]>
    setProgresses(flightId: string, p: number[]) {
        this.progresses[flightId] = p
        this.emit(flightId, p)
    }
}

export const progressController = new ProgressEmitter()

export const useVideoColorfinderProgress = (flightId: string) => {
    const [processingProgress, setProcessingProgress] = useState(
        progressController.progresses[flightId]?.[0] || 0
    )
    const [uploadProgress, setUploadProgress] = useState(
        progressController.progresses[flightId]?.[1] || 0
    )

    useEffect(() => {
        const listener = (p: number[]) => {
            setProcessingProgress(p[0])
            setUploadProgress(p[1])
        }
        progressController.addListener(flightId, listener)
        return () => {
            progressController.removeListener(flightId, listener)
        }
    }, [flightId])

    return { uploadProgress, processingProgress }
}

export const VideoColorfinderPreviewFiles = () => {
    const { uploads, job, flightId, useProcessing, selectFlightById, selectUploadSize } =
        useUploadFlowContext()
    const dispatch = useAppDispatch()

    const { uploadProgress, processingProgress } = useVideoColorfinderProgress(flightId)
    const uploadSize = useAppSelector(selectUploadSize(flightId))

    const [uploadData, setUploadData] = useState(!job?.mock || uploadProgress > 0)
    const [manualUpload, setManualUpload] = useState(false)

    const flight = useAppSelector(selectFlightById(flightId)) as Layer
    const isDemo = !!flight?.demoId && !!job
    // const uploadSize = useAppSelector(selectUploadSize(flightId))
    const uploadTimestamp = useAppSelector(selectUploadTimestampForFlightId(flightId))

    const { processCallback, uploadCallback, dependencies } = useProcessing((p: number[]) => {
        progressController.setProgresses(flightId, p)
    })

    const summary = getSummaryFromUploads(uploads)
    const canUpload =
        !summary.stateCount?.[ImageProcessingState.PendingUpload] &&
        !summary.stateCount?.[ImageProcessingState.Uploading] &&
        !!summary.stateCount?.[ImageProcessingState.ProcessingDone]

    const isProcessingDone =
        job?.status === JobStatusType.COMPLETED ||
        job?.status === JobStatusType.MOCKCOMPLETED ||
        job?.status === JobStatusType.QUEUED

    const canView = useMemo(
        () =>
            uploads.every(
                (upload) => "state" in upload && upload.state >= ImageProcessingState.ProcessingDone
            ),
        [uploads]
    )

    const {
        loading: processInProgress,
        createJob,
        error,
    } = useAsyncCallback(
        {
            createJob: async () => {
                dispatch(
                    addUploadTimestamp({
                        flightId,
                        timestamp: new Date().toISOString(),
                    })
                )
                await processCallback({ upload: uploadData })
            },
        },
        [...dependencies, flightId, uploadData]
    )
    const processStarted = useMemo(() => {
        return !!(processInProgress || error || job)
    }, [processInProgress, error, job])

    const exceededUsage = flightExceededUsage[flightId]
    const hasExceededUploads = exceededUsage > 0

    const [videoFile, srtFile] = useMemo(() => {
        return [
            uploads.find((upload) => {
                const isSRT = get(upload, "isSRT", false)
                return !isSRT
            }),
            uploads.find((upload) => {
                const isSRT = get(upload, "isSRT", false)
                return isSRT
            }),
        ]
    }, [uploads])

    const uploadingDone = uploadProgress === 1
    const isUploading = uploadProgress > 0 || manualUpload

    const isNotUploaded = isProcessingDone && !uploadingDone && !isUploading

    const videoName = get(videoFile, "name") as string
    const srtName = get(srtFile, "name") as string

    if (isDemo) return <VideoColorfinderPreviewFilesDemo />

    return (
        <>
            <div className="border-b border-gray-700" />
            {!browserSupported && (
                <div className="text-[#C04949]">
                    Browser does not support Colorfinder. Please use Chrome.
                </div>
            )}
            {canUpload && !processStarted && !isProcessingDone && (
                <div className="my-2 flex flex-col gap-2">
                    <div className="flex justify-between">
                        <h5 className="text-gray-0">Ready to Upload</h5>
                        <h5 className="text-gray-100">{`${uploads.length} files`}</h5>
                    </div>
                    <div className="my-2 flex flex-col gap-1">
                        <FileItem
                            flightId={flightId}
                            name={videoName || "Video File Missing"}
                            id={videoFile?.id}
                            isError={!videoFile}
                        />
                        <FileItem
                            flightId={flightId}
                            name={srtName || "SRT File Missing"}
                            id={srtFile?.id}
                            isWarning={!srtFile}
                        />
                    </div>
                </div>
            )}
            {(!videoFile || !srtFile) && !processStarted && !isProcessingDone && (
                <FlightUploadDropbox isAddMoreFiles />
            )}
            {processStarted && !isProcessingDone && job && !error && !manualUpload && (
                <ProgressBar
                    progress={processingProgress}
                    total={1}
                    timeElapsed={job?.createdAt as unknown as string}
                />
            )}
            {uploadData && !uploadingDone && isProcessingDone && !error && (
                <UploadProgressBar
                    progress={uploadProgress}
                    total={1}
                    timeElapsed={uploadTimestamp}
                />
            )}
            {error && (
                <div className="mb-4">
                    <div className="flex items-center mt-4 mb-4 gap-1">
                        <Icons.SmallIcon Icon={Icons.Error} className="text-red-500" />
                        <h5 className="text-gray-0">Processing Failed</h5>
                    </div>
                </div>
            )}
            {isProcessingDone && !uploadData && (
                <div className="flex items-center gap-1 my-4">
                    <Icons.Icon Icon={Icons.Checkmark} className="text-green-500" />
                    <h5 className="text-gray-0">Processing Complete</h5>
                </div>
            )}
            {uploadingDone && uploadData && isProcessingDone && (
                <div className="flex items-center gap-1 my-4">
                    <Icons.Icon Icon={Icons.Checkmark} className="text-green-500" />
                    <h5 className="text-gray-0">Upload Complete</h5>
                </div>
            )}
            {hasExceededUploads && <GenericDataUsage remainingBytes={exceededUsage} />}
            <div className="mt-2">
                <InspectorPanelMetadata
                    entity={flight as SidePanelLayer}
                    uploads={uploads as GenericUpload[]}
                    uploadSize={uploadSize}
                    completedUploadCount={summary.stateCount[ImageProcessingState.UploadDone] || 0}
                    hideCreator
                    hideFileCount={true}
                    className=""
                    isSingleFile
                />
                {!isUploading && !isProcessingDone && (
                    <>
                        <div className="border-b border-gray-700" />
                        <div className="my-4">
                            {!processStarted && (
                                <InspectorPanelKeyValueToggle
                                    toggleClassName="justify-between"
                                    isToggled={uploadData}
                                    setIsToggled={() => setUploadData((e) => !e)}
                                    keyTitle="Upload Data"
                                    disabled={processStarted}
                                />
                            )}
                            {!uploadData && (
                                <small className="text-orange-500 mt-1">
                                    {"Processed video won't be saved"}
                                </small>
                            )}
                        </div>
                    </>
                )}
                <div className="border-b border-gray-700" />
                {/* action buttons */}
                {canView && isProcessingDone && (
                    <ButtonDynamic
                        isTertiary={
                            (!processStarted && !isProcessingDone) ||
                            (uploadData && !uploadingDone) ||
                            isNotUploaded
                        }
                        title={"View Color Data"}
                        RightIcon={Icons.ArrowRight}
                        className="w-full !px-0 mt-2"
                        onClick={() => {
                            dispatch(openColorFinder({ flightId }))
                        }}
                        disabled={!videoFile}
                    />
                )}
                <div className="flex gap-2 items-center mt-2">
                    <DeleteFlight />
                    {canUpload && !isUploading && (
                        <ButtonPrimary
                            title={isNotUploaded ? "Upload" : "Process"}
                            className="w-full !px-0"
                            onClick={
                                isNotUploaded && !!uploadCallback
                                    ? () => {
                                          dispatch(
                                              addUploadTimestamp({
                                                  flightId,
                                                  timestamp: new Date().toISOString(),
                                              })
                                          )
                                          setManualUpload(true)
                                          setUploadData(true)
                                          uploadCallback(flightId)
                                      }
                                    : () => createJob()
                            }
                            disabled={(processStarted || !videoFile) && !isNotUploaded}
                        />
                    )}
                </div>
            </div>
        </>
    )
}

const FileItem = ({
    id,
    flightId,
    name,
    isError,
    isWarning,
    isDemo,
}: {
    id?: string
    flightId: string
    name: string
    isError?: boolean
    isWarning?: boolean
    isDemo?: boolean
}) => {
    const dispatch = useAppDispatch()
    return (
        <div className="flex gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
                {isError ? (
                    <Icons.SmallIcon Icon={Icons.Error} className="text-red-500" />
                ) : isWarning ? (
                    <Icons.SmallIcon Icon={Icons.Error} className="text-orange-500" />
                ) : (
                    <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />
                )}
                <small className="text-gray-100 normalLineHeight">
                    {trimMiddle(name, { maxLength: 25 })}
                </small>
            </div>
            {id && !isDemo && (
                <Icons.ExtraSmallIcon
                    Icon={Icons.Trash}
                    className="text-gray-300 hover:text-red-500 cursor-pointer"
                    onClick={() => dispatch(deleteUpload({ uploadId: id, setId: flightId }))}
                />
            )}
        </div>
    )
}
